/**
 * ONCALL-SCHED-01a (2026-09-15) — the on-call schedule for the service techs.
 *
 * Seth: "We need an oncall builder/scheduler. The techs come in for meetings
 * every 3 months and put together who is primary and who is backup. We then
 * let the on call answering service know… a tech might be on call a couple
 * days not a full week. Can the manager go day by day and fill in as he goes?"
 *
 * The unit is a DAY (`pegasus_oncall_day`: primary + optional backup); a
 * PERIOD (`pegasus_oncall_period`) is the stretch the manager is filling —
 * typically three months, any dates. Drafts stay in the office; Publish makes
 * the days visible on the phone, Today and the Board. A day's on-call runs
 * from the office opening that morning to the next opening (business_hours),
 * so 2 AM still belongs to yesterday's pair — `oncallDateFor()` here and
 * `pegasus_oncall_at()` in the database apply the same rule.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { useAllActiveTechs, type ActiveTech } from "@/features/board/api";
import { useBusinessHours } from "@/features/customer-360/serviceTypes";
import { useOrgSettingJson } from "@/lib/orgSettings";

export interface OncallPeriod {
  id: string;
  starts_on: string; // YYYY-MM-DD
  ends_on: string;
  label: string | null;
  status: "draft" | "published";
  published_at: string | null;
  published_by: string | null;
  sent_to_ceas_at: string | null;
  sent_to_ceas_by: string | null;
  created_at: string;
  created_by: string | null;
  updated_at: string;
}

export type OncallSource = "meeting" | "edit" | "swap" | "fill" | "import";

export interface OncallDay {
  day: string; // YYYY-MM-DD
  period_id: string | null;
  primary_empno: string;
  backup_empno: string | null;
  note: string | null;
  source: OncallSource;
  updated_at: string;
  updated_by: string | null;
}

/** On call for a given day, as every chip/banner shows it. */
export interface OncallAt {
  oncall_day: string;
  primary_empno: string;
  backup_empno: string | null;
  primary_name: string;
  backup_name: string | null;
  note: string | null;
}

// ── dates ───────────────────────────────────────────────────────────────────
export const isoDate = (d: Date) => d.toLocaleDateString("en-CA");
export const parseISO = (iso: string) => new Date(`${iso}T00:00:00`);
export const addDaysISO = (iso: string, n: number) => { const d = parseISO(iso); d.setDate(d.getDate() + n); return isoDate(d); };
export const addMonthsISO = (iso: string, n: number) => { const d = parseISO(iso); d.setMonth(d.getMonth() + n); return isoDate(d); };
export const monthStartISO = (iso: string) => iso.slice(0, 8) + "01";
export const monthEndISO = (iso: string) => { const d = parseISO(monthStartISO(iso)); d.setMonth(d.getMonth() + 1); d.setDate(0); return isoDate(d); };
/** Fri/Sat/Sun — the weekend stretch on the sheet. */
export const isWeekendISO = (iso: string) => { const w = parseISO(iso).getDay(); return w === 0 || w === 5 || w === 6; };
export const fmtShort = (iso: string) => parseISO(iso).toLocaleDateString("en-US", { month: "short", day: "numeric" });
export const fmtWeekday = (iso: string) => parseISO(iso).toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
export const fmtMonth = (iso: string) => parseISO(iso).toLocaleDateString("en-US", { month: "long", year: "numeric" });

/** Label a period the way the sheet is titled: "Aug 1 – Oct 31, 2026". */
export function periodLabel(startISO: string, endISO: string): string {
  const s = parseISO(startISO), e = parseISO(endISO);
  const sm = s.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  const em = e.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  return s.getFullYear() === e.getFullYear()
    ? `${sm} – ${em}, ${e.getFullYear()}`
    : `${sm}, ${s.getFullYear()} – ${em}, ${e.getFullYear()}`;
}

/**
 * The on-call DATE in effect at a moment: today, unless it is before the
 * office opens — then yesterday's pair is still carrying the phone.
 */
export function oncallDateFor(now: Date, openHour: number): string {
  const d = new Date(now);
  if (d.getHours() < openHour) d.setDate(d.getDate() - 1);
  return isoDate(d);
}

/** "tonight" while the office is open on a weekday; "now" after hours / weekends. */
export function oncallPhase(now: Date, openHour: number, closeHour: number): "tonight" | "now" {
  const w = now.getDay();
  if (w === 0 || w === 6) return "now";
  const h = now.getHours();
  return h >= openHour && h < closeHour ? "tonight" : "now";
}

// ── service techs (department 03 — the only ones on the rotation) ──────────
export const SERVICE_DEPT = "03";
export function techFirstName(t: Pick<ActiveTech, "firstname" | "empname" | "empno">): string {
  return (t.firstname ?? "").trim() || (t.empname ?? "").trim().split(/\s+/)[0] || t.empno;
}
export function useServiceTechs() {
  const q = useAllActiveTechs();
  const techs = (q.data ?? []).filter((t) => t.dept === SERVICE_DEPT).sort((a, b) => a.empno.localeCompare(b.empno));
  return { ...q, techs };
}

// ── periods ─────────────────────────────────────────────────────────────────
export function useOncallPeriods() {
  return useQuery({
    queryKey: ["oncall_periods"],
    staleTime: 60 * 1000,
    queryFn: async (): Promise<OncallPeriod[]> => {
      const { data, error } = await supabase.from("pegasus_oncall_period").select("*").order("starts_on", { ascending: false });
      if (error) throw error;
      return (data ?? []) as OncallPeriod[];
    },
  });
}

async function whoAmI(): Promise<string> {
  return (await supabase.auth.getUser()).data.user?.email ?? "office";
}

export function useCreateOncallPeriod() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: { starts_on: string; ends_on: string; label?: string | null }): Promise<OncallPeriod> => {
      const who = await whoAmI();
      const { data, error } = await supabase.from("pegasus_oncall_period")
        .insert({ starts_on: v.starts_on, ends_on: v.ends_on, label: v.label ?? periodLabel(v.starts_on, v.ends_on), created_by: who })
        .select("*").single();
      if (error) throw error;
      return data as OncallPeriod;
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["oncall_periods"] }),
  });
}

export function useUpdateOncallPeriod() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: { id: string; starts_on?: string; ends_on?: string; label?: string | null }) => {
      const patch: Record<string, unknown> = {};
      if (v.starts_on) patch.starts_on = v.starts_on;
      if (v.ends_on) patch.ends_on = v.ends_on;
      if (v.label !== undefined) patch.label = v.label;
      const { error } = await supabase.from("pegasus_oncall_period").update(patch).eq("id", v.id);
      if (error) throw error;
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["oncall_periods"] }),
  });
}

export function usePublishOncallPeriod() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const who = await whoAmI();
      const { error } = await supabase.from("pegasus_oncall_period")
        .update({ status: "published", published_at: new Date().toISOString(), published_by: who }).eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["oncall_periods"] });
      void qc.invalidateQueries({ queryKey: ["oncall_days"] });
      void qc.invalidateQueries({ queryKey: ["oncall_at"] });
    },
  });
}

export function useDeleteOncallPeriod() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("pegasus_oncall_period").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["oncall_periods"] });
      void qc.invalidateQueries({ queryKey: ["oncall_days"] });
      void qc.invalidateQueries({ queryKey: ["oncall_at"] });
    },
  });
}

// ── days ────────────────────────────────────────────────────────────────────
export function useOncallDays(startISO: string, endISO: string, enabled = true) {
  return useQuery({
    queryKey: ["oncall_days", startISO, endISO],
    enabled,
    staleTime: 30 * 1000,
    queryFn: async (): Promise<OncallDay[]> => {
      const { data, error } = await supabase.from("pegasus_oncall_day").select("*")
        .gte("day", startISO).lte("day", endISO).order("day");
      if (error) throw error;
      return (data ?? []) as OncallDay[];
    },
  });
}

export interface OncallDayInput {
  day: string;
  primary_empno: string;
  backup_empno: string | null;
  note?: string | null;
  source?: OncallSource;
}

export function useUpsertOncallDays() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (rows: OncallDayInput[]) => {
      if (rows.length === 0) return;
      const who = await whoAmI();
      const payload = rows.map((r) => ({
        day: r.day,
        primary_empno: r.primary_empno,
        backup_empno: r.backup_empno && r.backup_empno !== r.primary_empno ? r.backup_empno : null,
        note: r.note ?? null,
        source: r.source ?? "edit",
        updated_by: who,
        created_by: who,
      }));
      const { error } = await supabase.from("pegasus_oncall_day").upsert(payload, { onConflict: "day" });
      if (error) throw error;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["oncall_days"] });
      void qc.invalidateQueries({ queryKey: ["oncall_at"] });
      void qc.invalidateQueries({ queryKey: ["oncall_gaps"] });
    },
  });
}

export function useClearOncallDays() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (days: string[]) => {
      if (days.length === 0) return;
      const { error } = await supabase.from("pegasus_oncall_day").delete().in("day", days);
      if (error) throw error;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["oncall_days"] });
      void qc.invalidateQueries({ queryKey: ["oncall_at"] });
      void qc.invalidateQueries({ queryKey: ["oncall_gaps"] });
    },
  });
}

// ── who is on call (chips, banners) ─────────────────────────────────────────
/** The published pair for one calendar date (Board day header). */
export function useOncallForDate(dateISO: string) {
  return useQuery({
    queryKey: ["oncall_at", "date", dateISO],
    staleTime: 60 * 1000,
    queryFn: async (): Promise<OncallAt | null> => {
      const { data, error } = await supabase.from("pegasus_oncall_day")
        .select("day, primary_empno, backup_empno, note, pegasus_oncall_period!inner(status)")
        .eq("day", dateISO).eq("pegasus_oncall_period.status", "published").maybeSingle();
      if (error) throw error;
      if (!data) return null;
      const row = data as unknown as { day: string; primary_empno: string; backup_empno: string | null; note: string | null };
      return { oncall_day: row.day, primary_empno: row.primary_empno, backup_empno: row.backup_empno, primary_name: row.primary_empno, backup_name: row.backup_empno, note: row.note };
    },
  });
}

/** The pair carrying the phone right now (or tonight), by the office-open rule. */
export function useOncallNow() {
  return useQuery({
    queryKey: ["oncall_at", "now"],
    refetchInterval: 5 * 60 * 1000,
    queryFn: async (): Promise<OncallAt | null> => {
      const { data, error } = await supabase.rpc("pegasus_oncall_at");
      if (error) throw error;
      const rows = (data ?? []) as OncallAt[];
      return rows[0] ?? null;
    },
  });
}

/** A tech's own on-call days (published only): the last week (so the block
 * they are in reads whole — "Sep 14 – 15", not just today) and the next 120 days. */
export function useMyOncallDays(empno: string | null) {
  const today = isoDate(new Date());
  const from = addDaysISO(today, -7);
  const to = addDaysISO(today, 120);
  return useQuery({
    queryKey: ["oncall_at", "mine", empno, from],
    enabled: !!empno,
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<OncallDay[]> => {
      const { data, error } = await supabase.from("pegasus_oncall_day")
        .select("day, period_id, primary_empno, backup_empno, note, source, updated_at, updated_by, pegasus_oncall_period!inner(status)")
        .eq("pegasus_oncall_period.status", "published")
        .gte("day", from).lte("day", to)
        .or(`primary_empno.eq.${empno},backup_empno.eq.${empno}`)
        .order("day");
      if (error) throw error;
      return (data ?? []) as unknown as OncallDay[];
    },
  });
}

/** Group consecutive days with the same pair into blocks ("Sep 21 – 22 · Primary"). */
export interface OncallBlock { start: string; end: string; primary_empno: string; backup_empno: string | null; days: number }
export function blocksOf(days: OncallDay[]): OncallBlock[] {
  const out: OncallBlock[] = [];
  for (const d of [...days].sort((a, b) => a.day.localeCompare(b.day))) {
    const last = out[out.length - 1];
    if (last && addDaysISO(last.end, 1) === d.day && last.primary_empno === d.primary_empno && (last.backup_empno ?? null) === (d.backup_empno ?? null)) {
      last.end = d.day; last.days += 1;
    } else {
      out.push({ start: d.day, end: d.day, primary_empno: d.primary_empno, backup_empno: d.backup_empno ?? null, days: 1 });
    }
  }
  return out;
}
export const blockLabel = (b: OncallBlock) => b.start === b.end ? fmtWeekday(b.start) : `${fmtShort(b.start)} – ${fmtShort(b.end)}`;

/** Days per tech for a fairness strip (the strip counts; the room decides). */
export interface FairnessRow { empno: string; primary: number; backup: number; weekend: number; holidays: number }
export function fairness(days: OncallDay[], empnos: string[], holidays: Set<string>): FairnessRow[] {
  const m = new Map<string, FairnessRow>(empnos.map((e) => [e, { empno: e, primary: 0, backup: 0, weekend: 0, holidays: 0 }]));
  const get = (e: string) => { let r = m.get(e); if (!r) { r = { empno: e, primary: 0, backup: 0, weekend: 0, holidays: 0 }; m.set(e, r); } return r; };
  for (const d of days) {
    const p = get(d.primary_empno);
    p.primary += 1;
    if (isWeekendISO(d.day)) p.weekend += 1;
    if (holidays.has(d.day)) p.holidays += 1;
    if (d.backup_empno) get(d.backup_empno).backup += 1;
  }
  return [...m.values()];
}

/** The office-open / close hours the on-call day rule reads. */
export function useOncallHours() {
  const bh = useBusinessHours();
  return { open: Number(bh.data?.start ?? 7), close: Number(bh.data?.end ?? 17) };
}

// ── ONCALL-SCHED-01b — conflicts (coverage gaps) and swaps ──────────────────
/** A published on-call day whose primary/backup is out that day. approved = gap; requested = waiting on the office. */
export interface OncallGap {
  day: string;
  role: "primary" | "backup";
  empno: string;
  tech_name: string;
  avail_id: string;
  kind: string;
  status: "approved" | "requested";
  all_day: boolean;
  title: string | null;
  start_at: string;
  end_at: string;
}

export function useOncallGaps(fromISO: string, toISO: string, enabled = true) {
  return useQuery({
    queryKey: ["oncall_gaps", fromISO, toISO],
    enabled,
    staleTime: 60 * 1000,
    queryFn: async (): Promise<OncallGap[]> => {
      const { data, error } = await supabase.rpc("pegasus_oncall_gaps", { p_from: fromISO, p_to: toISO });
      if (error) throw error;
      return (data ?? []) as OncallGap[];
    },
  });
}

/** Published days only (what techs and the approval card should read). */
export function useOncallDaysPublished(fromISO: string, toISO: string, enabled = true) {
  return useQuery({
    queryKey: ["oncall_days", "published", fromISO, toISO],
    enabled: enabled && fromISO <= toISO,
    staleTime: 60 * 1000,
    queryFn: async (): Promise<OncallDay[]> => {
      const { data, error } = await supabase.from("pegasus_oncall_day")
        .select("day, period_id, primary_empno, backup_empno, note, source, updated_at, updated_by, pegasus_oncall_period!inner(status)")
        .eq("pegasus_oncall_period.status", "published")
        .gte("day", fromISO).lte("day", toISO).order("day");
      if (error) throw error;
      return (data ?? []) as unknown as OncallDay[];
    },
  });
}

/** The tech's published on-call days inside a date range — the phone's time-off warning. */
export function myOncallDaysIn(days: OncallDay[], empno: string | null, fromISO: string, toISO: string): { day: string; role: "primary" | "backup" }[] {
  if (!empno) return [];
  return days
    .filter((d) => d.day >= fromISO && d.day <= toISO && (d.primary_empno === empno || d.backup_empno === empno))
    .map((d) => ({ day: d.day, role: d.primary_empno === empno ? "primary" : "backup" }));
}

export interface OncallSwapRequest {
  id: string;
  empno: string;
  from_day: string;
  to_day: string;
  role: "primary" | "backup";
  with_empno: string | null;
  note: string | null;
  status: "requested" | "approved" | "denied";
  decided_by: string | null;
  decided_at: string | null;
  decision_note: string | null;
  created_at: string;
}

export function useOncallSwapRequests(status: "requested" | "all" = "requested") {
  return useQuery({
    queryKey: ["oncall_swaps", status],
    refetchInterval: 60 * 1000,
    queryFn: async (): Promise<OncallSwapRequest[]> => {
      let q = supabase.from("pegasus_oncall_swap_request").select("*").order("from_day");
      if (status === "requested") q = q.eq("status", "requested").gte("to_day", isoDate(new Date()));
      else q = q.gte("to_day", addDaysISO(isoDate(new Date()), -30));
      const { data, error } = await q;
      if (error) throw error;
      return (data ?? []) as OncallSwapRequest[];
    },
  });
}

export function useRequestOncallSwap() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: { empno: string; from_day: string; to_day: string; role: "primary" | "backup"; with_empno: string | null; note: string | null }) => {
      const who = await whoAmI();
      const { error } = await supabase.from("pegasus_oncall_swap_request").insert({ ...v, created_by: who });
      if (error) throw error;
      // The DB trigger DMs everyone who can act on it.
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["oncall_swaps"] }),
  });
}

/** Approve = put the cover tech on those days (in the requester's role) and mark it; deny = mark it with a note. */
export function useDecideOncallSwap() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: { req: OncallSwapRequest; decision: "approved" | "denied"; cover_empno?: string | null; note?: string | null }) => {
      const who = await whoAmI();
      if (v.decision === "approved") {
        if (!v.cover_empno) throw new Error("Pick who covers before approving.");
        const { data, error: e1 } = await supabase.from("pegasus_oncall_day").select("*")
          .gte("day", v.req.from_day).lte("day", v.req.to_day);
        if (e1) throw e1;
        const rows = ((data ?? []) as OncallDay[])
          .filter((d) => (v.req.role === "primary" ? d.primary_empno === v.req.empno : d.backup_empno === v.req.empno))
          .map((d) => ({
            day: d.day,
            primary_empno: v.req.role === "primary" ? (v.cover_empno as string) : d.primary_empno,
            backup_empno: v.req.role === "backup" ? v.cover_empno : (d.backup_empno === v.cover_empno ? null : d.backup_empno),
            note: d.note,
            source: "swap" as const,
            updated_by: who,
            created_by: who,
          }));
        if (rows.length) {
          const { error: e2 } = await supabase.from("pegasus_oncall_day").upsert(rows, { onConflict: "day" });
          if (e2) throw e2;
        }
      }
      const { error } = await supabase.from("pegasus_oncall_swap_request").update({
        status: v.decision,
        with_empno: v.decision === "approved" ? v.cover_empno : v.req.with_empno,
        decided_by: who,
        decided_at: new Date().toISOString(),
        decision_note: v.note?.trim() || null,
      }).eq("id", v.req.id);
      if (error) throw error;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["oncall_swaps"] });
      void qc.invalidateQueries({ queryKey: ["oncall_days"] });
      void qc.invalidateQueries({ queryKey: ["oncall_at"] });
      void qc.invalidateQueries({ queryKey: ["oncall_gaps"] });
    },
  });
}

export const roleWord = (r: "primary" | "backup") => (r === "primary" ? "primary" : "backup");

// ── ONCALL-SCHED-01c — the CEAS handoff ─────────────────────────────────────
export function useMarkSentToCeas() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const who = await whoAmI();
      const { error } = await supabase.from("pegasus_oncall_period")
        .update({ sent_to_ceas_at: new Date().toISOString(), sent_to_ceas_by: who }).eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["oncall_periods"] }),
  });
}

/** Days changed after the sheet last went to CEAS (never sent → every day). */
export function changedSinceSent(period: OncallPeriod, days: OncallDay[]): OncallDay[] {
  if (!period.sent_to_ceas_at) return days;
  return days.filter((d) => d.updated_at > (period.sent_to_ceas_at as string));
}


// ── ONCALL-SCHED-01d — the tech's own calendar on the phone ─────────────────
/** An all-day calendar file for one on-call block (iOS/Android open it in the Calendar app). */
export function oncallBlockICS(b: { start: string; end: string }, title: string, description: string): string {
  const d = (iso: string) => iso.replace(/-/g, "");
  const stamp = new Date().toISOString().replace(/[-:]/g, "").slice(0, 15) + "Z";
  const uid = `oncall-${b.start}-${b.end}@pegasus.sedgwickheating.com`;
  return [
    "BEGIN:VCALENDAR", "VERSION:2.0", "PRODID:-//Sedgwick Heating//Pegasus On-Call//EN", "CALSCALE:GREGORIAN",
    "BEGIN:VEVENT", `UID:${uid}`, `DTSTAMP:${stamp}`,
    `DTSTART;VALUE=DATE:${d(b.start)}`, `DTEND;VALUE=DATE:${d(addDaysISO(b.end, 1))}`,
    `SUMMARY:${title.replace(/[,;]/g, " ")}`, `DESCRIPTION:${description.replace(/[,;]/g, " ").replace(/\n/g, "\\n")}`,
    "END:VEVENT", "END:VCALENDAR",
  ].join("\r\n");
}
export const icsHref = (ics: string) => "data:text/calendar;charset=utf-8," + encodeURIComponent(ics);

/** Whole days until a block starts (0 = today, negative = already running). */
export function daysUntil(iso: string): number {
  const a = parseISO(isoDate(new Date())).getTime();
  const b = parseISO(iso).getTime();
  return Math.round((b - a) / 86400000);
}


// ── ONCALL-PHONE-01 — which answering-service calls reach the techs' phones ──
// Seth 2026-09-23: "For on call on the mobile app — maybe we only show same day
// and sales requests from the on call center. Hold for offices don't need to be
// shown to the techs." parse-oncall gives every call exactly ONE class tag; the
// office picks which classes show on the phone (Settings → On-Call), so the
// rule changes without a code edit. Default: Same Day + Sales, last 3 days —
// long enough to cover a holiday weekend, short enough that a page the office
// never cleared stops following the techs around (they stay on Today).

/** The answering service's call classes — parse-oncall tags one per call. */
export type OncallClass = "same_day" | "sales" | "service_request" | "hold_for_office" | "plumbing";

export const ONCALL_CLASS_OPTIONS: { key: OncallClass; label: string; hint: string }[] = [
  { key: "same_day", label: "Same Day", hint: "Urgent — the answering service paged the on-call tech." },
  { key: "sales", label: "Sales", hint: "Someone asking about new equipment or a quote." },
  { key: "service_request", label: "Service Request (Not Urgent)", hint: "Needs a visit, but it can wait for the office." },
  { key: "hold_for_office", label: "Hold for Office", hint: "The caller asked for the office to call back." },
  { key: "plumbing", label: "Plumbing", hint: "Routed to the plumber." },
];

const CLASS_KEYS = new Set<string>(ONCALL_CLASS_OPTIONS.map((o) => o.key));

/** The class the answering service gave the call. A tech's "Hold For Office"
 *  adds the hold tag on top of a same-day call, so the service's own class
 *  wins (same order the office's Today panel reads). */
export function oncallClassOf(tags: string[] | null | undefined): OncallClass {
  const t = tags ?? [];
  for (const k of ["same_day", "service_request", "plumbing", "sales"] as const) if (t.includes(k)) return k;
  return "hold_for_office";
}

/** A tech (not the answering service) held this call for the office. */
export function heldByTech(tags: string[] | null | undefined): boolean {
  const t = tags ?? [];
  return t.includes("hold_for_office") && oncallClassOf(t) !== "hold_for_office";
}

export interface OncallPhoneRules {
  classes: OncallClass[];
  /** Calls older than this many days drop off the phones (they stay on Today). */
  days: number;
}
export const DEFAULT_ONCALL_PHONE_RULES: OncallPhoneRules = { classes: ["same_day", "sales"], days: 3 };
export const ONCALL_PHONE_RULES_KEY = "oncall_phone_rules";

/** Clean up whatever is stored (a missing key, an empty list, a bad number) into usable rules. */
export function normalizeOncallPhoneRules(v: Partial<OncallPhoneRules> | null | undefined): OncallPhoneRules {
  const classes = Array.isArray(v?.classes) ? v!.classes.filter((k): k is OncallClass => CLASS_KEYS.has(k)) : [];
  const n = Number(v?.days);
  return {
    classes: classes.length ? classes : DEFAULT_ONCALL_PHONE_RULES.classes,
    days: Number.isFinite(n) && n >= 1 ? Math.min(30, Math.round(n)) : DEFAULT_ONCALL_PHONE_RULES.days,
  };
}

export function useOncallPhoneRules(): OncallPhoneRules & { isLoading: boolean } {
  const q = useOrgSettingJson<Partial<OncallPhoneRules>>(ONCALL_PHONE_RULES_KEY, DEFAULT_ONCALL_PHONE_RULES);
  return { ...normalizeOncallPhoneRules(q.data), isLoading: q.isLoading };
}
